'use client';

import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import { rankNumeralClassName } from '@/components/social/shared/rank-numeral';
import { Icon } from '@/components/ui/icon';
import { StarAward01IconSolid } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type RankBadgeSize = 'compact' | 'display';
interface RankBadgeProps {
  rank: number;
  /**
   * `display` is the default table / board rank size. `compact` is one step
   * smaller for very tight rails.
   */
  size?: RankBadgeSize | undefined;
  /**
   * When true (DataTable rank column): trophy-only for tiers 1–3 (gold / silver /
   * bronze tint). Pass `trophyAriaLabel` for screen readers.
   */
  table?: boolean | undefined;
  /** Required when `table` — e.g. `useTranslations('ui.data_table')('rank_trophy_aria', { rank })`. */
  trophyAriaLabel?: string | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SHIMMER_TIER_CLASS: Record<1 | 2 | 3, string> = {
  1: 'morfi-rank-shiny-text morfi-rank-shiny-gold',
  2: 'morfi-rank-shiny-text morfi-rank-shiny-silver',
  3: 'morfi-rank-shiny-text morfi-rank-shiny-bronze'
};
const RANK_TINT_CLASS: Record<1 | 2 | 3, string> = {
  1: 'text-yellow-500 dark:text-yellow-400',
  2: 'text-foreground-secondary',
  3: 'text-amber-800 dark:text-amber-600'
};
export function RankBadge(t0) {
  const $ = _c(11);
  const {
    rank,
    size: t1,
    table: t2,
    trophyAriaLabel
  } = t0;
  const size = t1 === undefined ? "display" : t1;
  const table = t2 === undefined ? false : t2;
  if (rank < 1 || rank > 3) {
    return null;
  }
  const tier = rank as 1 | 2 | 3;
  let t3;
  if ($[0] !== size || $[1] !== tier) {
    t3 = cn(rankNumeralClassName(size), SHIMMER_TIER_CLASS[tier]);
    $[0] = size;
    $[1] = tier;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const numeralClass = t3;
  if (table) {
    const label = trophyAriaLabel !== undefined && trophyAriaLabel !== "" ? trophyAriaLabel : `Rank ${String(rank)}`;
    const t4 = RANK_TINT_CLASS[tier];
    let t5;
    if ($[3] !== t4) {
      t5 = cn("size-3.5 shrink-0", t4);
      $[3] = t4;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== label || $[6] !== t5) {
      t6 = <Icon icon={StarAward01IconSolid} className={t5} aria-label={label} />;
      $[5] = label;
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  let t4;
  if ($[8] !== numeralClass || $[9] !== rank) {
    t4 = <span className={numeralClass}>{rank}</span>;
    $[8] = numeralClass;
    $[9] = rank;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}